import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useState, useContext } from 'react';

import {
  JURISDICTION_TABS,
  ORDER_BY_PARAM_MAP,
  REVERSE_ORDER_BY_MAP,
  ORDER_BY_DISPLAY_MAP,
  ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP,
  ORAL_ARGUMENTS_REVERSE_ORDER_BY_MAP,
  JUDGE_SEARCH_ORDER_BY_PARAM_MAP,
  JUDGE_SEARCH_REVERSE_ORDER_BY_MAP,
} from './constants';
import jurisdictionsData from './jurisdictions.json';
import {
  type AdvancedFiltersForm,
  type AppliedFilterItem,
  type ClassActionSearchCard,
  type CourtCitation,
  type CourtTypeParam,
  type JurisdictionItem,
  type JurisdictionsData,
  type JurisdictionTabKey,
  type RecapDocument,
  type SelectedJurisdictions,
} from './types';

export const JURISDICTIONS_DATA = jurisdictionsData as JurisdictionsData;

const STATE_COURT_PREFIX = 'state-';

export const getJurisdictionsForTab = (
  tabKey: JurisdictionTabKey
): JurisdictionItem[] => {
  let rawJurisdictions: JurisdictionItem[] = [];

  switch (tabKey) {
    case 'federal_appellate':
      rawJurisdictions = JURISDICTIONS_DATA.federal_appellate.circuits;
      break;
    case 'federal_district':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.federal_district.miscellaneous_courts,
        ...JURISDICTIONS_DATA.federal_district.historical_courts,
      ];
      break;
    case 'bankruptcy':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_appellate_panels,
        ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_district_courts,
      ];
      break;
    case 'state':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.state.state_courts,
        ...JURISDICTIONS_DATA.state.territory_courts,
      ];
      break;
    case 'more':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.more.military_courts,
        ...JURISDICTIONS_DATA.more.tribal_courts,
        ...JURISDICTIONS_DATA.more.miscellaneous_courts,
        ...JURISDICTIONS_DATA.more.historical_courts,
      ];
      break;
    default:
      return [];
  }

  // De-duplicate within tab
  const uniqueMap = new Map<string, JurisdictionItem>();
  rawJurisdictions.forEach((jurisdiction) => {
    if (!uniqueMap.has(jurisdiction.id)) {
      uniqueMap.set(jurisdiction.id, jurisdiction);
    }
  });

  return Array.from(uniqueMap.values());
};

export const getAllJurisdictions = (): JurisdictionItem[] => {
  const allJurisdictions = JURISDICTION_TABS.flatMap((tab) =>
    getJurisdictionsForTab(tab.key as JurisdictionTabKey)
  );

  const uniqueMap = new Map<string, JurisdictionItem>();
  allJurisdictions.forEach((jurisdiction) => {
    if (!uniqueMap.has(jurisdiction.id)) {
      uniqueMap.set(jurisdiction.id, jurisdiction);
    }
  });

  return Array.from(uniqueMap.values());
};

export const createDefaultSelectedJurisdictions = (): SelectedJurisdictions => {
  const allJurisdictions = getAllJurisdictions();
  const selected: SelectedJurisdictions = {};

  allJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = true;
  });

  return selected;
};

export const createSelectedJurisdictionsForTab = (
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const allJurisdictions = getAllJurisdictions();
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const selected: SelectedJurisdictions = {};

  allJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = false;
  });

  tabJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = true;
  });

  return selected;
};

export const getSelectedJurisdictionsCount = (
  selected: SelectedJurisdictions
): number => {
  return Object.values(selected).filter(Boolean).length;
};

export const getTotalJurisdictionsCount = (): number => {
  return getAllJurisdictions().length;
};

export const areAllJurisdictionsSelected = (
  selected: SelectedJurisdictions
): boolean => {
  const totalCount = getTotalJurisdictionsCount();
  const selectedCount = getSelectedJurisdictionsCount(selected);
  return selectedCount === totalCount;
};

export const filterJurisdictionsBySearch = (
  jurisdictions: JurisdictionItem[],
  searchQuery: string
): JurisdictionItem[] => {
  if (!searchQuery.trim()) {
    return jurisdictions;
  }

  const query = searchQuery.toLowerCase();
  return jurisdictions.filter(
    (jurisdiction) =>
      jurisdiction.name.toLowerCase().includes(query) ||
      jurisdiction.id.toLowerCase().includes(query)
  );
};

export const toggleJurisdiction = (
  currentSelected: SelectedJurisdictions,
  jurisdictionId: string
): SelectedJurisdictions => {
  // Prevent prototype pollution
  if (
    !jurisdictionId ||
    typeof jurisdictionId !== 'string' ||
    jurisdictionId.startsWith('__') ||
    jurisdictionId === 'constructor' ||
    jurisdictionId === 'prototype'
  ) {
    return currentSelected;
  }

  return {
    ...currentSelected,
    [jurisdictionId]: !currentSelected[jurisdictionId],
  };
};

export const clearAllJurisdictions = (): SelectedJurisdictions => {
  return Object.fromEntries(
    getAllJurisdictions().map((jurisdiction) => [jurisdiction.id, false])
  );
};

export const selectCurrentTabJurisdictions = (
  currentSelected: SelectedJurisdictions,
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const tabUpdates = Object.fromEntries(
    tabJurisdictions.map((jurisdiction) => [jurisdiction.id, true])
  );
  return { ...currentSelected, ...tabUpdates };
};

export const clearCurrentTabJurisdictions = (
  currentSelected: SelectedJurisdictions,
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const tabUpdates = Object.fromEntries(
    tabJurisdictions.map((jurisdiction) => [jurisdiction.id, false])
  );
  return { ...currentSelected, ...tabUpdates };
};

const getValidCourtIds = (): string[] => {
  try {
    const allJurisdictions = getAllJurisdictions();
    return allJurisdictions
      .filter((court) => !court.id.startsWith(STATE_COURT_PREFIX))
      .map((court) => court.id);
  } catch {
    return [];
  }
};

const VALID_COURT_IDS = getValidCourtIds();

export const isValidCourtId = (courtId: string): boolean => {
  return VALID_COURT_IDS.includes(courtId);
};

export const filterValidCourtIds = (courtIds: string[]): string[] => {
  return courtIds.filter(isValidCourtId);
};

export const buildCourtUrlParameter = (
  selected: SelectedJurisdictions,
  typeParam?: CourtTypeParam
): string => {
  const allJurisdictions = getAllJurisdictions();

  const selectedIds = allJurisdictions
    .filter((jurisdiction) => selected[jurisdiction.id])
    .map((jurisdiction) => jurisdiction.id);

  // Only validate court IDs for Cases API (PACER), not for Opinions API
  if (typeParam === 'r') {
    const validCourtIdsOnly = filterValidCourtIds(selectedIds);
    return validCourtIdsOnly.join(' ');
  }
  return selectedIds.join(' ');
};

export const getOrderByParam = (
  option: string,
  isOralArguments = false,
  isJudgeSearch = false
): string => {
  if (isJudgeSearch) {
    const mapped =
      JUDGE_SEARCH_ORDER_BY_PARAM_MAP[
        option as keyof typeof JUDGE_SEARCH_ORDER_BY_PARAM_MAP
      ];
    if (mapped) return mapped;
    if (option.includes(' ') || option.includes(',')) return option;
    return JUDGE_SEARCH_ORDER_BY_PARAM_MAP.score_desc;
  }
  if (isOralArguments) {
    const mapped =
      ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP[
        option as keyof typeof ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP
      ];
    if (mapped) return mapped;
    if (option.includes(' ')) return option;
    return ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP.score_desc;
  }
  const mapped = ORDER_BY_PARAM_MAP[option as keyof typeof ORDER_BY_PARAM_MAP];
  if (mapped) return mapped;
  if (option.includes(' ')) return option;
  return ORDER_BY_PARAM_MAP.score_desc;
};

export const getUIOrderByValue = (
  orderBy: string,
  isOralArguments = false,
  isJudgeSearch = false
): string => {
  if (isJudgeSearch)
    return JUDGE_SEARCH_REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
  if (isOralArguments)
    return ORAL_ARGUMENTS_REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
  return REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
};

export const getOrderByDisplayText = (orderBy: string): string => {
  return ORDER_BY_DISPLAY_MAP[orderBy] || orderBy;
};

// CourtListener field prefixes - do not wrap in quotes (breaks field:value syntax)
const FIELDED_QUERY_PREFIXES = [
  'court_id:',
  'docketNumber:',
  'caseName:',
  'judge:',
  'assignedTo:',
  'citation:',
];

const isFieldedQuery = (query: string): boolean => {
  const lower = query.toLowerCase();
  return FIELDED_QUERY_PREFIXES.some((prefix) =>
    lower.startsWith(prefix.toLowerCase())
  );
};

export const processExactMatchQuery = (
  query: string,
  exactMatch: boolean
): string => {
  if (!query.trim()) {
    return query;
  }

  const trimmedQuery = query.trim();
  const isAlreadyQuoted =
    trimmedQuery.startsWith('"') &&
    trimmedQuery.endsWith('"') &&
    trimmedQuery.length >= 2;

  // Fielded queries (court_id:, docketNumber:, etc.) must not be wrapped in quotes
  // or the CourtListener API treats them as phrase search instead of field query
  if (isFieldedQuery(trimmedQuery)) {
    return trimmedQuery;
  }

  if (exactMatch) {
    return isAlreadyQuoted ? trimmedQuery : `"${trimmedQuery}"`;
  }
  return isAlreadyQuoted ? trimmedQuery.slice(1, -1) : trimmedQuery;
};

export const cleanOpinionType = (type: string): string => {
  if (!type) {
    return 'Unknown Opinion';
  }

  let cleaned = type
    .replace(/-/g, ' ')
    .replace(/[^a-zA-Z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  cleaned = cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  if (
    !cleaned.toLowerCase().includes('opinion') &&
    type.toLowerCase() !== 'headmatter'
  ) {
    cleaned += ' Opinion';
  }

  return cleaned;
};

/**
 * Sanitizes HTML from trusted API source (Court Listener) for safe injection.
 * Strips dangerous elements and attributes while preserving links and formatting.
 * Defense-in-depth: even trusted sources are sanitized to harden against API changes.
 */
const sanitizeHtmlForTrustedApi = (tempDiv: HTMLDivElement): void => {
  // Remove dangerous tags (preserves links, formatting, citations)
  const dangerousTags = ['script', 'iframe', 'object', 'embed', 'form'];
  dangerousTags.forEach((tag) => {
    tempDiv.querySelectorAll(tag).forEach((el) => el.remove());
  });

  // Remove event handler attributes from all elements
  tempDiv.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('on') && attr.name.length > 2) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // Sanitize href: block javascript: and data: URLs, preserve http/https and relative
  tempDiv.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href');
    if (href && /^\s*(javascript|data):/i.test(href)) {
      link.removeAttribute('href');
    }
  });
};

export const processHtmlWithCitations = (html: string): string => {
  const COURT_LISTENER_BASE_URL = 'https://www.courtlistener.com';

  if (!html) {
    return html;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Content from trusted API source (Court Listener). Sanitized for defense-in-depth.
  sanitizeHtmlForTrustedApi(tempDiv);

  const links = tempDiv.querySelectorAll('a[href]');
  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/')) {
      link.setAttribute('href', `${COURT_LISTENER_BASE_URL}${href}`);
    }
  });

  return tempDiv.innerHTML;
};

export const formatClusterCitations = (
  citations: CourtCitation[] | undefined
): string | null => {
  if (!citations || citations.length === 0) return null;
  return citations.map((c) => `${c.volume} ${c.reporter} ${c.page}`).join(', ');
};

export const isEmpty = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value))
  );
};

export const normalizeAdvancedFilters = (
  raw: AdvancedFiltersForm | Record<string, unknown>,
  isOralArguments = false,
  isJudgeSearch = false
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (!raw) {
    return result;
  }

  Object.entries(raw).forEach(([key, value]) => {
    if (isEmpty(value)) {
      return;
    }

    if (key === 'order_by') {
      if (isJudgeSearch) {
        result.order_by =
          JUDGE_SEARCH_ORDER_BY_PARAM_MAP[
            value as string as keyof typeof JUDGE_SEARCH_ORDER_BY_PARAM_MAP
          ] || (value as string);
      } else if (isOralArguments) {
        result.order_by =
          ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP[
            value as string as keyof typeof ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP
          ] || (value as string);
      } else {
        result.order_by =
          ORDER_BY_PARAM_MAP[
            value as string as keyof typeof ORDER_BY_PARAM_MAP
          ] || (value as string);
      }
      return;
    }

    if (
      key === 'jurisdictions' ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value;
    }
  });

  return result;
};

const addOrderByParam = (
  params: string[],
  typeParam: CourtTypeParam,
  orderBy: string
): void => {
  // Skip citeCount for Cases (type 'r') as it causes API failures
  if (typeParam === 'r' && orderBy.includes('citeCount')) {
    params.push('order_by=score%20desc');
  } else {
    const encodedOrderBy = orderBy.split(' ').join('%20');
    params.push(`order_by=${encodedOrderBy}`);
  }
};

const addDefaultPrecedentialStatus = (
  params: string[],
  additionalParams: Record<string, unknown>
): void => {
  const hasCustomPrecedentialStatus = Object.keys(additionalParams).some(
    (key) => key.startsWith('stat_')
  );

  if (!hasCustomPrecedentialStatus) {
    params.push('stat_Published=on');
    params.push('stat_Unpublished=on');
  }
};

const addFilterParams = (
  params: string[],
  typeParam: CourtTypeParam,
  additionalParams: Record<string, unknown>
): void => {
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (['order_by', 'jurisdictions'].includes(key)) {
      return;
    }

    if (key === 'court') {
      if (typeof value === 'string' && value.trim()) {
        if (typeParam === 'r') {
          const courtIds = value.trim().split(/\s+/);
          const validCourts = filterValidCourtIds(courtIds);
          if (validCourts.length > 0) {
            params.push(`court=${encodeURIComponent(validCourts.join(' '))}`);
          }
        } else {
          params.push(`court=${encodeURIComponent(value.trim())}`);
        }
      }
      return;
    }

    if (value === null || value === undefined || value === '') {
      return;
    }

    if (key === 'judge') {
      const judgeValue = encodeURIComponent(String(value));
      const paramName = typeParam === 'r' ? 'assigned_to' : 'judge';
      params.push(`${paramName}=${judgeValue}`);
      return;
    }

    // Skip party_name and atty_name for opinions API
    if (typeParam === 'o' && (key === 'party_name' || key === 'atty_name')) {
      return;
    }

    if (typeof value === 'boolean') {
      if (value) {
        params.push(`${key}=on`);
      }
      return;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      params.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  });
};

const addJurisdictionParams = (
  params: string[],
  typeParam: CourtTypeParam,
  additionalParams: Record<string, unknown>
): void => {
  const jurisdictions = additionalParams.jurisdictions as SelectedJurisdictions;
  if (jurisdictions) {
    const selectedCount = getSelectedJurisdictionsCount(jurisdictions);
    const allSelected = areAllJurisdictionsSelected(jurisdictions);

    if (!allSelected && selectedCount > 0) {
      const courtParameter = buildCourtUrlParameter(jurisdictions, typeParam);
      if (courtParameter) {
        params.push(`court=${encodeURIComponent(courtParameter)}`);
      }
    }
  }
};

export const buildCourtSearchParams = (
  typeParam: CourtTypeParam,
  query: string,
  orderBy: string,
  additionalParams: Record<string, unknown> = {}
): string => {
  const params: string[] = [];

  params.push(`type=${typeParam}`);
  addOrderByParam(params, typeParam, orderBy);

  if (query && query.trim().length > 0) {
    params.push(`q=${encodeURIComponent(query.trim())}`);
  }

  addDefaultPrecedentialStatus(params, additionalParams);
  addFilterParams(params, typeParam, additionalParams);
  addJurisdictionParams(params, typeParam, additionalParams);

  return params.join('&');
};

export const buildAppliedFilters = (
  searchQueryValue: string,
  filtersInput: AdvancedFiltersForm,
  exactMatch: boolean = false
): AppliedFilterItem[] => {
  const items: AppliedFilterItem[] = [];
  const filters = filtersInput || {};

  const jurisdictions = filters.jurisdictions;
  if (jurisdictions && !areAllJurisdictionsSelected(jurisdictions)) {
    items.push({
      label: 'Courts',
      value: `${getSelectedJurisdictionsCount(jurisdictions)} selected`,
    });
  } else {
    items.push({ label: 'Courts', value: 'All' });
  }

  if (searchQueryValue && searchQueryValue.trim()) {
    items.push({ label: 'Query', value: searchQueryValue.trim() });
  }

  if (exactMatch) {
    items.push({ label: 'Search Mode', value: 'Exact Match' });
  }

  const addIfValid = (label: string, v: unknown) => {
    if (!isEmpty(v)) {
      items.push({ label, value: String(v) });
    }
  };

  addIfValid('Judge/Assigned To', filters.judge);
  addIfValid('Case Name', filters.case_name);
  addIfValid('Docket Number', filters.docket_number);
  addIfValid('Party Name (Cases)', filters.party_name);
  addIfValid('Attorney Name (Cases)', filters.atty_name);

  return items;
};

export const scrollToTop = (delay = 150): void => {
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, delay);
};

// =============================================================================
// Date Formatting Utilities
// =============================================================================

/**
 * Formats a date string to include time in "Month Day, Year, h:mm a.m./p.m." format
 * @param dateString - The date string to format
 * @returns Formatted date string with time or 'Not Available' if date is null
 */
export const formatCourtDateWithTime = (dateString: string | null): string => {
  if (!dateString) {
    return 'Not Available';
  }

  const date = new Date(dateString);

  // Extract parts using Intl with "en-US" but force UTC,
  // then adjust manually by the offset in the string.
  const isoMatch = dateString.match(/([+-]\d{2}):(\d{2})$/);
  const offsetMinutes = isoMatch
    ? parseInt(isoMatch[1]) * 60 +
      parseInt(isoMatch[2]) * (isoMatch[1].startsWith('-') ? -1 : 1)
    : 0;

  // Get UTC time in ms
  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;

  // Apply original offset to get the "as-written" time
  const targetTime = new Date(utcTime + offsetMinutes * 60000);

  let formatted = targetTime.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    // hour: "numeric",
    // minute: "2-digit",
    hour12: true,
  });

  // Normalize AM/PM
  formatted = formatted.replace('AM', 'a.m.').replace('PM', 'p.m.');

  return formatted;
};

// =============================================================================
// Pagination Utilities
// =============================================================================

const extractPageNumberFromUrl = (
  url: string | null,
  // eslint-disable-next-line no-unused-vars
  logError?: (message: string, error: Error) => void
): number | null => {
  if (!url) {
    return null;
  }

  try {
    // For full URLs
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : null;
    }
    // For relative URLs
    else if (url.includes('page=')) {
      const pageMatch = url.match(/page=(\d+)/);
      return pageMatch ? parseInt(pageMatch[1], 10) : null;
    }
    return null;
  } catch (e) {
    if (logError) {
      logError('Error extracting page number from url', e as Error);
    } else {
      console.error('Error extracting page number from url', e);
    }
    return null;
  }
};

export const useDocketEntriesPagination = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { logError } = useContext(AppConfig);

  const handlePreviousPage = (previousUrl: string | null) => {
    // If previous URL is null but we're not on page 1, go to page 1
    if (!previousUrl) {
      // We're likely on page 2, so go to page 1
      setCurrentPage(1);
      return;
    }

    const prevPage = extractPageNumberFromUrl(previousUrl, logError);
    if (prevPage) {
      setCurrentPage(prevPage);
    } else {
      // If we can't extract a page number but have a URL, default to page 1
      setCurrentPage(1);
    }

    scrollToTop();
  };

  const handleNextPage = (nextUrl: string | null) => {
    if (!nextUrl) {
      return;
    } // No next page available

    const nextPage = extractPageNumberFromUrl(nextUrl, logError);
    if (nextPage) {
      setCurrentPage(nextPage);
    } else {
      logError(
        'Could not extract next page number from URL:',
        new Error(nextUrl || 'No URL provided')
      );
    }
    scrollToTop();
  };

  return {
    currentPage,
    handlePreviousPage,
    handleNextPage,
  };
};

// =============================================================================
// Phone & Attorney Formatting Utilities
// =============================================================================

export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }

  return phone;
};

interface ParsedAttorneyContact {
  address: string[];
  phone: string | null;
  email: string | null;
}

export const parseAttorneyContact = (
  contact: string
): ParsedAttorneyContact => {
  const lines = contact.split('\n').filter((line) => line.trim());
  const result: ParsedAttorneyContact = {
    address: [],
    phone: null,
    email: null,
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    const phoneMatch = trimmedLine.match(/\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/);
    if (phoneMatch && !result.phone) {
      result.phone = formatPhoneNumber(phoneMatch[0]);
      continue;
    }

    const emailMatch = trimmedLine.match(
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/
    );
    if (emailMatch && !result.email) {
      result.email = emailMatch[0];
      continue;
    }

    result.address.push(trimmedLine);
  }

  return result;
};

// =============================================================================
// Docket Entry Utilities
// =============================================================================

export const formatSimpleDate = (dateString: string | null): string => {
  if (!dateString) {
    return 'Not Available';
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
  } catch {
    return 'Invalid Date';
  }
};

export const isMainDocument = (doc: RecapDocument): boolean => {
  return doc.attachment_number === null || doc.attachment_number === undefined;
};

// =============================================================================
// Class action lawsuit search (client-side sort by filed_on)
// =============================================================================

export const parseClassActionFiledOn = (dateStr: string): number => {
  const t = dateStr?.trim() ?? '';
  if (!t) {
    return 0;
  }
  const parsed = Date.parse(t);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    return new Date(year, month - 1, day).getTime();
  }
  return 0;
};

/**
 * Sorts cards by `filed_on` (M/D/YYYY or parseable date). Default: newest first.
 */
export const sortClassActionCardsByFiledOn = (
  cards: ClassActionSearchCard[],
  sortOption: string
): ClassActionSearchCard[] => {
  const copy = [...cards];
  const oldestFirst = sortOption === 'dateFiled_asc';
  copy.sort((a, b) => {
    const ta = parseClassActionFiledOn(a.filed_on);
    const tb = parseClassActionFiledOn(b.filed_on);
    if (ta !== tb) {
      return oldestFirst ? ta - tb : tb - ta;
    }
    return a.docket_id - b.docket_id;
  });
  return copy;
};
